/*
 * Lemonade Training Oy copy rights all right reserved.
 * Box Firestore Helper Functions
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  getCountFromServer,
  query,
  where,
  orderBy,
  limit,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  collectionGroup,
  setDoc,
} from "firebase/firestore";
import { firebaseDB, COLLECTIONS } from "../store/firebaseApp";
import type {
  Box,
  BoxMember,
  BoxProgram,
  BoxInvitation,
  BoxRole,
  BoxMemberStatus,
} from "@lemonadetraining/contracts";
import type { Invite } from "@lemonadetraining/contracts";

/* =======================================
 * Box Collection Helpers
 * ====================================== */

/**
 * Get a box by ID
 * @param boxID - Box document ID
 * @returns Box document or null if not found
 */
export const getBox = async (boxID: string): Promise<(Box & { id: string }) | null> => {
  try {
    const boxRef = doc(firebaseDB, COLLECTIONS.Boxes, boxID);
    const boxSnap = await getDoc(boxRef);

    if (boxSnap.exists()) {
      return { id: boxSnap.id, ...boxSnap.data() } as Box & { id: string };
    }
    return null;
  } catch (error) {
    console.error(`Error getting box ${boxID}:`, error);
    throw error;
  }
};

/** The server's own page-name rule; anything else cannot address a storefront. */
const PAGE_NAME_REGEX = /^[a-z0-9-]+$/;

/**
 * The gym's public storefront page, or null when it has none to send anyone to.
 *
 * `active` is filtered in memory rather than in the query on purpose: a page
 * is created with only `{name, ownerUID, boxID}`, so the field does not exist
 * until an owner has toggled visibility at least once. `where("active","==",
 * true)` would therefore hide every storefront nobody has ever touched, which
 * is most of them. `active !== false` is the same reading the owner's own
 * settings screen uses.
 *
 * A denied or failed read is null, not a throw: a member with no storefront to
 * renew from should see one button fewer, never an error about our plumbing.
 */
export const getActiveLandingPageForBox = async (
  boxID: string
): Promise<{ name: string } | null> => {
  try {
    // Two, so a box carrying one hidden page and one live page still resolves.
    const pagesQuery = query(
      collection(firebaseDB, COLLECTIONS.LandingPage),
      where("boxID", "==", boxID),
      limit(2)
    );
    const snapshot = await getDocs(pagesQuery);
    for (const pageDoc of snapshot.docs) {
      const data = pageDoc.data() as { name?: unknown; active?: unknown };
      if (data.active === false) continue;
      if (typeof data.name !== "string" || !PAGE_NAME_REGEX.test(data.name)) continue;
      return { name: data.name };
    }
    return null;
  } catch (error) {
    console.error(`Error getting landing page for box ${boxID}:`, error);
    return null;
  }
};

/**
 * Get all boxes (with optional filters)
 * @param options - Query options
 * @returns Array of box documents
 */
export const getBoxes = async (options?: {
  limit?: number;
  orderBy?: { field: keyof Box; direction: "asc" | "desc" };
}): Promise<Array<Box & { id: string }>> => {
  try {
    const boxesRef = collection(firebaseDB, COLLECTIONS.Boxes);
    const constraints = [];

    if (options?.orderBy) {
      constraints.push(orderBy(options.orderBy.field, options.orderBy.direction));
    }

    if (options?.limit) {
      constraints.push(limit(options.limit));
    }

    const q = query(boxesRef, ...constraints);

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as Box & { id: string });
  } catch (error) {
    console.error("Error getting boxes:", error);
    throw error;
  }
};

/* Box updates go through the updateBox Cloud Function (Admin SDK, field
 * allowlist), never a direct client write: the Boxes rules now deny any client
 * write that touches stripeAccountID, and a blanket client-side updateDoc is
 * the mass-assignment vector that allowlist exists to close. A previous
 * `updateBoxDirect` helper here had zero callers and was removed rather than
 * left as a footgun. */

/* =======================================
 * Box Members Collection Helpers
 * ====================================== */

/**
 * Get box members collection reference
 * @param boxID - Box document ID
 */
export const getBoxMembersRef = (boxID: string) => {
  return collection(firebaseDB, COLLECTIONS.Boxes, boxID, COLLECTIONS.BoxMembers);
};

/**
 * Get all members of a box
 *
 * Every filter combination here sorts by `joinedAt`, so each one needs its own
 * composite index on the Members collection — the equality fields must form an
 * unbroken prefix ahead of the sort field, which means an index carrying an
 * extra field in between (role, status, joinedAt) does NOT serve a role-only
 * query. Deployed shapes, all in `firestore.indexes.json`:
 *
 *   no filter          → single-field joinedAt index (automatic)
 *   role               → (role, joinedAt DESC)          — also serves `role in [...]`
 *   status             → (status, joinedAt DESC)
 *   role + status      → (role, status, joinedAt DESC)
 *
 * Adding a new filter combination means adding its index AND a case to the
 * staging index guard — the emulator does not enforce indexes, so a missing
 * one passes every local and PR check and fails only on real Firebase.
 *
 * Not the only caller of these shapes: the People roster builds LIVE, limited
 * variants of the same queries (an `onSnapshot` per staff role, and the
 * pending staff invites), because these one-shot getters cannot serve a
 * listener with a page size. Widening a filter here means checking there too.
 *
 * @param boxID - Box document ID
 * @param filters - Optional filters
 * @returns Array of box member documents
 */
export const getBoxMembers = async (
  boxID: string,
  filters?: {
    role?: BoxRole | BoxRole[];
    status?: BoxMemberStatus;
  }
): Promise<Array<BoxMember & { id: string }>> => {
  try {
    const membersRef = getBoxMembersRef(boxID);
    const constraints = [];

    if (filters?.role) {
      constraints.push(
        Array.isArray(filters.role)
          ? where("role", "in", filters.role)
          : where("role", "==", filters.role)
      );
    }

    if (filters?.status) {
      constraints.push(where("status", "==", filters.status));
    }

    constraints.push(orderBy("joinedAt", "desc"));

    const q = query(membersRef, ...constraints);

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as BoxMember & { id: string }
    );
  } catch (error) {
    console.error(`Error getting box members for ${boxID}:`, error);
    throw error;
  }
};

/**
 * Count members on the server: one read per 1,000 matching members, instead
 * of downloading every document to count it (getBoxMembers). Equality-only
 * filters, so Firestore serves them from its single-field indexes; no sort,
 * so no joinedAt composite is needed.
 */
export const countBoxMembers = async (
  boxID: string,
  filters?: { role?: BoxRole; status?: BoxMemberStatus }
): Promise<number> => {
  const constraints = [];
  if (filters?.role) constraints.push(where("role", "==", filters.role));
  if (filters?.status) constraints.push(where("status", "==", filters.status));
  const snapshot = await getCountFromServer(query(getBoxMembersRef(boxID), ...constraints));
  return snapshot.data().count;
};

/**
 * Get a specific box member
 * @param boxID - Box document ID
 * @param memberUID - User UID (member document ID)
 * @returns Box member document or null
 */
export const getBoxMember = async (
  boxID: string,
  memberUID: string
): Promise<(BoxMember & { id: string }) | null> => {
  try {
    const memberRef = doc(firebaseDB, COLLECTIONS.Boxes, boxID, COLLECTIONS.BoxMembers, memberUID);
    const memberSnap = await getDoc(memberRef);

    if (memberSnap.exists()) {
      return { id: memberSnap.id, ...memberSnap.data() } as BoxMember & { id: string };
    }
    return null;
  } catch (error) {
    console.error(`Error getting box member ${memberUID} for box ${boxID}:`, error);
    throw error;
  }
};

/**
 * Add a member to a box
 * @param boxID - Box document ID
 * @param userUID - User UID (will be used as document ID)
 * @param memberData - Member data
 */
export const addBoxMember = async (
  boxID: string,
  userUID: string,
  memberData: Omit<BoxMember, "userUID">
): Promise<void> => {
  try {
    const memberRef = doc(firebaseDB, COLLECTIONS.Boxes, boxID, COLLECTIONS.BoxMembers, userUID);
    await setDoc(memberRef, {
      userUID,
      ...memberData,
      joinedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error(`Error adding box member ${userUID} to box ${boxID}:`, error);
    throw error;
  }
};

/**
 * Update a box member
 * @param boxID - Box document ID
 * @param memberUID - User UID (member document ID)
 * @param updateData - Partial member data to update
 */
export const updateBoxMember = async (
  boxID: string,
  memberUID: string,
  updateData: Partial<Omit<BoxMember, "userUID">>
): Promise<void> => {
  try {
    const memberRef = doc(firebaseDB, COLLECTIONS.Boxes, boxID, COLLECTIONS.BoxMembers, memberUID);
    await updateDoc(memberRef, updateData);
  } catch (error) {
    console.error(`Error updating box member ${memberUID} for box ${boxID}:`, error);
    throw error;
  }
};

/**
 * Remove a member from a box
 * @param boxID - Box document ID
 * @param memberUID - User UID (member document ID)
 */
export const removeBoxMember = async (boxID: string, memberUID: string): Promise<void> => {
  try {
    const memberRef = doc(firebaseDB, COLLECTIONS.Boxes, boxID, COLLECTIONS.BoxMembers, memberUID);
    await deleteDoc(memberRef);
  } catch (error) {
    console.error(`Error removing box member ${memberUID} from box ${boxID}:`, error);
    throw error;
  }
};

/**
 * Get all boxes a user belongs to (using collection group query)
 * @param userUID - User UID
 * @returns Array of box IDs with member info
 */
export const getUserBoxes = async (
  userUID: string
): Promise<Array<{ boxID: string; member: BoxMember }>> => {
  try {
    const membersRef = collectionGroup(firebaseDB, COLLECTIONS.BoxMembers);
    const q = query(membersRef, where("userUID", "==", userUID), where("status", "==", "active"));

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => {
      const pathParts = doc.ref.path.split("/");
      const boxID = pathParts[1]; // Boxes/{boxID}/Members/{userUID}
      return {
        boxID,
        member: doc.data() as BoxMember,
      };
    });
  } catch (error) {
    console.error(`Error getting boxes for user ${userUID}:`, error);
    throw error;
  }
};

/* =======================================
 * Box Programs Collection Helpers
 * ====================================== */

/**
 * Get box programs collection reference
 * @param boxID - Box document ID
 */
export const getBoxProgramsRef = (boxID: string) => {
  return collection(firebaseDB, COLLECTIONS.Boxes, boxID, COLLECTIONS.BoxPrograms);
};

/**
 * Get all programs for a box
 * @param boxID - Box document ID
 * @param filters - Optional filters
 * @returns Array of box program documents
 */
export const getBoxPrograms = async (
  boxID: string,
  filters?: {
    isActive?: boolean;
    accessLevel?: BoxProgram["accessLevel"];
  }
): Promise<Array<BoxProgram & { id: string }>> => {
  try {
    const programsRef = getBoxProgramsRef(boxID);
    const constraints = [];

    if (filters?.isActive !== undefined) {
      constraints.push(where("isActive", "==", filters.isActive));
    }

    if (filters?.accessLevel) {
      constraints.push(where("accessLevel", "==", filters.accessLevel));
    }

    constraints.push(orderBy("createdAt", "desc"));

    const q = query(programsRef, ...constraints);

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as BoxProgram & { id: string }
    );
  } catch (error) {
    console.error(`Error getting box programs for ${boxID}:`, error);
    throw error;
  }
};

/**
 * Get a specific box program
 * @param boxID - Box document ID
 * @param programUID - Program document ID
 * @returns Box program document or null
 */
export const getBoxProgram = async (
  boxID: string,
  programUID: string
): Promise<(BoxProgram & { id: string }) | null> => {
  try {
    const programRef = doc(
      firebaseDB,
      COLLECTIONS.Boxes,
      boxID,
      COLLECTIONS.BoxPrograms,
      programUID
    );
    const programSnap = await getDoc(programRef);

    if (programSnap.exists()) {
      return { id: programSnap.id, ...programSnap.data() } as BoxProgram & { id: string };
    }
    return null;
  } catch (error) {
    console.error(`Error getting box program ${programUID} for box ${boxID}:`, error);
    throw error;
  }
};

/**
 * Create a box program
 * @param boxID - Box document ID
 * @param programData - Program data
 * @returns Created program document ID
 */
export const createBoxProgram = async (
  boxID: string,
  programData: Omit<BoxProgram, "createdAt" | "updatedAt">
): Promise<string> => {
  try {
    const programsRef = getBoxProgramsRef(boxID);
    const newProgram = {
      ...programData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    const docRef = await addDoc(programsRef, newProgram);
    return docRef.id;
  } catch (error) {
    console.error(`Error creating box program for ${boxID}:`, error);
    throw error;
  }
};

/**
 * Update a box program
 * @param boxID - Box document ID
 * @param programUID - Program document ID
 * @param updateData - Partial program data to update
 */
export const updateBoxProgram = async (
  boxID: string,
  programUID: string,
  updateData: Partial<Omit<BoxProgram, "createdAt">>
): Promise<void> => {
  try {
    const programRef = doc(
      firebaseDB,
      COLLECTIONS.Boxes,
      boxID,
      COLLECTIONS.BoxPrograms,
      programUID
    );
    await updateDoc(programRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error(`Error updating box program ${programUID} for box ${boxID}:`, error);
    throw error;
  }
};

/* =======================================
 * Box Invitations Collection Helpers
 * ====================================== */

/**
 * Get box invitations from the unified Invites collection
 *
 * Sorted by `createdAt`, so each filter combination needs a composite index
 * whose equality fields sit ahead of the sort field. Only the fully-filtered
 * shape the Box Portal issues is deployed today —
 * (boxID, invitationType, status, boxRole, createdAt DESC) — so calling this
 * with fewer filters needs its own index plus a staging index-guard case
 * first; the emulator will happily run an unindexed query that real Firebase
 * rejects.
 *
 * @param boxID - Box document ID
 * @param filters - Optional filters
 * @returns Array of invitation documents from the unified Invites collection
 */
export const getBoxInvitationsFromUnified = async (
  boxID: string,
  filters?: {
    status?: "Pending" | "Accepted" | "Declined";
    role?: "coach" | "athlete";
  }
): Promise<Array<Invite & { id: string }>> => {
  try {
    const constraints = [where("boxID", "==", boxID), where("invitationType", "==", "toBox")];

    if (filters?.status) {
      constraints.push(where("status", "==", filters.status));
    }

    if (filters?.role) {
      constraints.push(where("boxRole", "==", filters.role));
    }

    constraints.push(orderBy("createdAt", "desc"));

    const q = query(collection(firebaseDB, COLLECTIONS.Invites), ...constraints);

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Invite & { id: string }
    );
  } catch (error) {
    console.error(`Error getting box invitations from unified collection for ${boxID}:`, error);
    throw error;
  }
};

/**
 * Get box invitations collection reference
 * @param boxID - Box document ID
 */
export const getBoxInvitationsRef = (boxID: string) => {
  return collection(firebaseDB, COLLECTIONS.Boxes, boxID, COLLECTIONS.BoxInvitations);
};

/**
 * Get invitations for a box
 * @param boxID - Box document ID
 * @param filters - Optional filters
 * @returns Array of invitation documents
 */
export const getBoxInvitations = async (
  boxID: string,
  filters?: {
    status?: BoxInvitation["status"];
    role?: "coach" | "athlete";
  }
): Promise<Array<BoxInvitation & { id: string }>> => {
  try {
    const invitationsRef = getBoxInvitationsRef(boxID);
    const constraints = [];

    if (filters?.status) {
      constraints.push(where("status", "==", filters.status));
    }

    if (filters?.role) {
      constraints.push(where("role", "==", filters.role));
    }

    constraints.push(orderBy("createdAt", "desc"));

    const q = query(invitationsRef, ...constraints);

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as BoxInvitation & { id: string }
    );
  } catch (error) {
    console.error(`Error getting box invitations for ${boxID}:`, error);
    throw error;
  }
};

/**
 * Create a box invitation
 * @param boxID - Box document ID
 * @param invitationData - Invitation data
 * @returns Created invitation document ID
 */
export const createBoxInvitation = async (
  boxID: string,
  invitationData: Omit<BoxInvitation, "createdAt" | "updatedAt">
): Promise<string> => {
  try {
    const invitationsRef = getBoxInvitationsRef(boxID);
    const newInvitation = {
      ...invitationData,
      status: "pending" as BoxInvitation["status"],
      createdAt: serverTimestamp(),
    };

    const docRef = await addDoc(invitationsRef, newInvitation);
    return docRef.id;
  } catch (error) {
    console.error(`Error creating box invitation for ${boxID}:`, error);
    throw error;
  }
};

/**
 * Update a box invitation
 * @param boxID - Box document ID
 * @param invitationUID - Invitation document ID
 * @param updateData - Partial invitation data to update
 */
export const updateBoxInvitation = async (
  boxID: string,
  invitationUID: string,
  updateData: Partial<Omit<BoxInvitation, "createdAt">>
): Promise<void> => {
  try {
    const invitationRef = doc(
      firebaseDB,
      COLLECTIONS.Boxes,
      boxID,
      COLLECTIONS.BoxInvitations,
      invitationUID
    );
    await updateDoc(invitationRef, {
      ...updateData,
      updatedAt: serverTimestamp(),
    });
  } catch (error) {
    console.error(`Error updating box invitation ${invitationUID} for box ${boxID}:`, error);
    throw error;
  }
};
